import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Share2, ArrowLeft, Settings, ExternalLink, Newspaper, Eye, Mail, Timer, Heart, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { socialPlatforms } from '../utils/socialPlatforms';
import { getEmbedUrl, getEmbedLabel } from '../utils/embedUtils';
import { getReadableIconColor } from '../utils/iconContrast';
import { getTheme } from '../utils/themes';
import { getFontById, getAnimationCSS, ANIMATION_KEYFRAMES, BUTTON_STYLE_OPTIONS, getAvatarShape } from '../utils/customisation';
import { getWallpaperCSS, getWallpaperElements } from '../utils/wallpapers';
import { MonkeyLogo } from '../components/MonkeyLogo';
import { ErrorBoundary } from '../components/ErrorBoundary';
import OrbitHeader from '../components/OrbitHeader';
import { StickerLayer } from '../components/StickerEditor';

import { ProfileBackgroundEffects, EXTRA_BG_KEYFRAMES } from '../components/profile/ProfileBackgroundEffects';
import { ProfileHeader } from '../components/profile/ProfileHeader';
import { ProfileWidgets } from '../components/profile/ProfileWidgets';
import { CategoryTabs, SocialIconsRow, LinkStack } from '../components/profile/ProfileLinkSections';
import { ProfileFooter } from '../components/profile/ProfileFooter';
import { api, getImageUrl } from '../utils/api';
import { useAuth } from '../App';

const PublicProfile = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hasDiaryPosts, setHasDiaryPosts] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [subEmail, setSubEmail] = useState('');
  const [subbing, setSubbing] = useState(false);
  const [subDone, setSubDone] = useState(false);
  const [subMsg, setSubMsg] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedTip, setSelectedTip] = useState(null);
  const auth = useAuth();
  const isOwnProfile = auth?.user?.username === username;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await api.getPublicProfile(username);
        setProfile(data);
        // Check if user has diary posts (for Fan Page button)
        api.getPublicDiary(username).then(posts => setHasDiaryPosts(posts?.length > 0)).catch(() => {});
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [username]);

  // Countdown timer
  useEffect(() => {
    if (!profile?.countdown_date) return;
    const update = () => {
      const target = new Date(profile.countdown_date).getTime();
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) { setCountdown(null); return; }
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000)
      });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [profile?.countdown_date]);

  const handleSubscribe = async () => {
    if (!subEmail.trim()) return;
    setSubbing(true);
    try {
      const resp = await api.publicSubscribe(username, subEmail.trim());
      setSubMsg(resp?.message || 'Almost done — check your inbox to confirm your subscription.');
      setSubDone(true);
      setSubEmail('');
    } catch { }
    finally { setSubbing(false); }
  };

  const handleLinkClick = (link) => {
    api.trackClick(username, link.id).catch(() => {});
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const handleTip = (amount) => {
    if (!profile.tip_jar_paypal) return;
    const note = encodeURIComponent(`Tip for @${username} via LinkMonkey`);
    const currency = profile.tip_jar_currency || 'EUR';
    // notify_url enables PayPal IPN: when the payment completes, PayPal
    // POSTs to our backend which credits the creator's balance.
    // `custom` carries the creator's user_id so we can attribute the tip
    // even if their tip_jar_paypal email is later changed.
    const notifyUrl = encodeURIComponent(`${window.location.origin}/api/paypal/ipn`);
    const customId = profile.user_id || '';
    const url = `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${encodeURIComponent(profile.tip_jar_paypal)}&item_name=${note}&amount=${amount}&currency_code=${currency}&no_shipping=1&notify_url=${notifyUrl}&custom=${encodeURIComponent(customId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Currency formatting helper used by Tip Jar render. Computed every render —
  // must use optional-chaining because this runs BEFORE the early return below
  // when `profile` is still null on first paint.
  const CURRENCY_SYMBOLS = { EUR: '€', USD: '$', GBP: '£', CAD: 'CA$', AUD: 'A$', JPY: '¥', CHF: 'CHF ', NZD: 'NZ$' };
  const tipCurrency = profile?.tip_jar_currency || 'EUR';
  const tipSymbol = CURRENCY_SYMBOLS[tipCurrency] || `${tipCurrency} `;
  const formatTip = (n) => {
    if (n == null || isNaN(n)) return '';
    // Whole numbers display without decimals (£100), fractional with 2 (£100.50)
    const fixed = Number(n).toFixed(2).replace(/\.00$/, '');
    return `${tipSymbol}${fixed}`;
  };

  const handleShare = async () => {
    // Always share the canonical branded URL — short, clean, recognisable on every platform.
    // React Helmet on the profile route sets per-user og:image / og:title / og:description
    // so unfurlers that render meta tags (X, Discord, iMessage, FB) show the rich preview.
    const shareUrl = `https://linkmonkey.io/${username}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile.name} on LinkMonkey`,
          text: `Check out ${profile.name}'s links`,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') copyToClipboard(shareUrl);
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8f9fa' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-primary animate-pulse mx-auto mb-4"></div>
          <p className="text-gray-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center px-4">
        <Card className="p-8 max-w-md w-full text-center">
          <MonkeyLogo size={40} className="mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2" data-testid="not-found-heading">Profile Not Found</h1>
          <p className="text-muted-foreground mb-6">The profile @{username} doesn't exist.</p>
          <Link to="/"><Button data-testid="go-home-btn">Go to Homepage</Button></Link>
        </Card>
      </div>
    );
  }

  const theme = getTheme(profile.theme);
  const ts = theme.styles;
  const profilePicUrl = getImageUrl(profile.profilePicture);

  // Detect dark theme by parsing background color luminance
  const isDarkTheme = (() => {
    const bg = ts['--theme-bg'] || '#ffffff';
    const hex = bg.replace('#', '');
    if (hex.length < 6) return false;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 128;
  })();

  const getIconColor = (color) => getReadableIconColor(color, ts['--theme-bg']);

  const embeddableLinks = profile.links?.filter(l => getEmbedUrl(l.url) && l.embed_enabled !== false && l.embed_enabled !== 'false') || [];
  const allLinks = profile.links || [];

  const displayName = profile.name || username || 'Profile';
  // Build a compelling description for unfurl previews:
  // 1) Their own bio if they wrote one
  // 2) Otherwise a sentence listing their actual platforms ("Follow Alex on Spotify, YouTube, TikTok…")
  // 3) Fallback to a generic but inviting line
  // NOTE: plain computation, not useMemo — we're below early returns above so hook
  // count would vary between renders (React error #310).
  const linkedPlatformNames = (() => {
    if (!profile?.links) return [];
    const SKIP = new Set(['website', 'custom', 'email', 'phone', 'sms', 'other']);
    const seen = new Set();
    const names = [];
    for (const l of profile.links) {
      const pid = l.platform;
      if (!pid || SKIP.has(pid) || seen.has(pid)) continue;
      seen.add(pid);
      const sp = socialPlatforms.find(p => p.id === pid);
      if (sp?.name) names.push(sp.name);
      if (names.length >= 5) break;
    }
    return names;
  })();

  const displayBio = (() => {
    const raw = (profile.bio || '').trim();
    if (raw) return raw;
    if (linkedPlatformNames.length >= 2) {
      const list = linkedPlatformNames.slice(0, 3).join(', ');
      const more = linkedPlatformNames.length > 3 ? ' and more' : '';
      return `Follow ${displayName} on ${list}${more} — all in one place.`;
    }
    return `Every link from ${displayName}, in one place.`;
  })();
  // Build OG image URL from the actual origin the page is being loaded from —
  // bullet-proof against REACT_APP_BACKEND_URL being misconfigured (which was
  // causing share previews to point at the preview backend on production).
  const ogImageUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/api/og/${username}.png`
    : `https://linkmonkey.io/api/og/${username}.png`;

  // Customisation
  const fontConfig = getFontById(profile.font);
  const anim = profile.animation || 'fade';
  const btnStyle = BUTTON_STYLE_OPTIONS.find(b => b.id === (profile.button_style || 'rounded')) || BUTTON_STYLE_OPTIONS[0];
  const isLeft = profile.layout === 'left';
  const accentOverride = profile.accent_color || null;
  const accent = accentOverride || ts['--theme-accent'];
  const wallpaperId = profile.wallpaper || 'none';
  const wallpaperCSS = getWallpaperCSS(wallpaperId, accent);
  const wallpaperEls = getWallpaperElements(wallpaperId);

  return (
    <div className={`min-h-screen ${wallpaperId !== 'none' ? 'wallpaper-container' : ''}`} style={{ background: ts['--theme-gradient'], backgroundColor: ts['--theme-bg'], fontFamily: `'${fontConfig.family}', sans-serif` }} data-testid="public-profile-page">
      <ProfileBackgroundEffects profile={profile} ts={ts} />
      {/* Load font + animation keyframes + wallpaper */}
      <Helmet>
        <title>{`${displayName} - LinkMonkey`}</title>
        <meta name="description" content={displayBio} />
        <meta property="og:title" content={`${displayName} · @${username}`} />
        <meta property="og:description" content={displayBio} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="LinkMonkey" />
        <meta property="og:url" content={`https://linkmonkey.io/${username}`} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${displayName} · @${username}`} />
        <meta name="twitter:description" content={displayBio} />
        <meta name="twitter:image" content={ogImageUrl} />
        {fontConfig.url && <link rel="stylesheet" href={fontConfig.url} />}
        <style>{ANIMATION_KEYFRAMES}{wallpaperCSS}{EXTRA_BG_KEYFRAMES}</style>
      </Helmet>

      {/* Wallpaper floating elements */}
      {wallpaperEls.map((el, i) => (
        <span key={i} className={el.className} aria-hidden="true">{el.char}</span>
      ))}

      {/* Owner nav bar */}
      {isOwnProfile && (
        <div className="fixed top-0 left-0 right-0 z-50 px-4 py-3 flex items-center justify-between" style={{ background: `${ts['--theme-bg']}cc`, backdropFilter: 'blur(12px)' }}>
          <Link to="/dashboard" className="flex items-center space-x-2 opacity-90 hover:opacity-100 transition-opacity" data-testid="profile-nav-back">
            <ArrowLeft className="w-4 h-4" style={{ color: ts['--theme-text'] }} />
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full"
              style={{ background: isDarkTheme ? 'rgba(255,255,255,0.92)' : 'rgba(20,20,20,0.92)' }}>
              <MonkeyLogo size={18} />
            </span>
            <span className="text-sm font-medium" style={{ color: ts['--theme-text'] }}>Dashboard</span>
          </Link>
          <Link to="/settings">
            <Button size="sm" variant="outline" className="text-xs" style={{ borderColor: ts['--theme-accent'], color: ts['--theme-accent'] }} data-testid="edit-profile-btn">
              <Settings className="w-3.5 h-3.5 mr-1.5" /> Edit Profile
            </Button>
          </Link>
        </div>
      )}

      <div className="wallpaper-content relative max-w-[440px] mx-auto px-4 pb-20" style={{ paddingTop: isOwnProfile ? '5rem' : '3rem' }}>
        {/* Pro stickers — decorative layer, never blocks link clicks */}
        <StickerLayer stickers={profile.stickers || []} />
        <ProfileHeader
          profile={profile}
          profilePicUrl={profilePicUrl}
          ts={ts}
          accent={accent}
          anim={anim}
          isLeft={isLeft}
          username={username}
          hasDiaryPosts={hasDiaryPosts}
          handleShare={handleShare}
        />

        <ProfileWidgets
          profile={profile}
          ts={ts}
          accent={accent}
          anim={anim}
          isLeft={isLeft}
          username={username}
          countdown={countdown}
          subEmail={subEmail}
          setSubEmail={setSubEmail}
          subbing={subbing}
          subDone={subDone}
          subMsg={subMsg}
          handleSubscribe={handleSubscribe}
          formatTip={formatTip}
          tipSymbol={tipSymbol}
          handleTip={handleTip}
        />

        <CategoryTabs allLinks={allLinks} activeCategory={activeCategory} setActiveCategory={setActiveCategory} ts={ts} accent={accent} anim={anim} isLeft={isLeft} />

        <SocialIconsRow allLinks={allLinks} activeCategory={activeCategory} profile={profile} ts={ts} anim={anim} isLeft={isLeft} btnStyle={btnStyle} getIconColor={getIconColor} handleLinkClick={handleLinkClick} />

        <LinkStack allLinks={allLinks} activeCategory={activeCategory} profile={profile} ts={ts} accent={accent} anim={anim} btnStyle={btnStyle} handleLinkClick={handleLinkClick} />

        {/* No links */}
        {(!profile.links || profile.links.length === 0) && (
          <div className="p-12 text-center rounded-xl" style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}` }} data-testid="no-links-card">
            <MonkeyLogo size={48} className="mx-auto mb-4" />
            <p style={{ color: ts['--theme-text-muted'] }}>No links added yet</p>
          </div>
        )}

        <ProfileFooter profile={profile} ts={ts} isDarkTheme={isDarkTheme} />
      </div>
    </div>
  );
};

export default PublicProfile;
